import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import Seo from '@/components/seo/Seo';
import { submitContact } from '@/lib/contactApi';

interface FormData {
  name: string;
  furigana: string;
  email: string;
  phone: string;
  category: string;
  content: string;
  privacyAgreed: boolean;
}

const labelMap: Record<string, string> = {
  name: '氏名',
  furigana: 'ふりがな',
  email: 'メールアドレス',
  phone: '電話番号',
  category: 'お問い合わせ種別',
  content: 'お問い合わせ内容',
};

const categoryLabels: Record<string, string> = {
  course: 'コースについて',
  pricing: '料金について',
  admission: '入学について',
  seminar: '体験講座について',
  corporate: '法人研修について',
  other: 'その他',
};

function formatValue(key: string, value: string | boolean): string {
  if (key === 'privacyAgreed') return '同意済み';
  if (key === 'category') return categoryLabels[value as string] || String(value);
  if (key === 'phone' && !value) return '（未入力）';
  return String(value || '');
}

export default function ContactConfirmPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const formData = (location.state as { formData?: FormData })?.formData;
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (!formData) {
      navigate('/contact', { replace: true });
    }
  }, [formData, navigate]);

  if (!formData) return null;

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError('');

    const payload: Record<string, string> = {};
    (Object.keys(formData) as (keyof FormData)[]).forEach((key) => {
      const val = formData[key];
      payload[key] = typeof val === 'boolean' ? (val ? '同意' : '未同意') : val;
    });

    try {
      const resp = await submitContact('contact', payload);

      if (resp.ok) {
        navigate('/contact/complete', { replace: true });
      } else {
        setSubmitError('送信に失敗しました。お手数ですが、再度お試しいただくか、お電話（03-6824-4086）にてお問い合わせください。');
      }
    } catch {
      setSubmitError('通信エラーが発生しました。ネットワーク接続をご確認のうえ、再度お試しください。');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo route="/contact/confirm" />
      <Header />
      <main className="pt-[120px] pb-20">
        <div className="max-w-[700px] mx-auto px-8">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-[13px] tracking-[0.06em] mb-10 text-foreground-400">
            <Link to="/" className="hover:text-accent-500 transition-colors">ホーム</Link>
            <span>&gt;</span>
            <Link to="/contact" className="hover:text-accent-500 transition-colors">お問い合わせ</Link>
            <span>&gt;</span>
            <span className="text-accent-600">確認</span>
          </div>

          <div className="text-center mb-12">
            <h1 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-4">
              お問い合わせ内容の確認
            </h1>
            <div className="gold-line mx-auto mb-4"></div>
            <p className="text-[16px] text-foreground-600 tracking-[0.04em]">
              以下の内容でお間違いなければ、「送信する」をクリックしてください。
            </p>
          </div>

          {/* Confirm Fields */}
          <div className="border border-background-200/80 bg-background-100/50 p-8 mb-8">
            <div className="space-y-5">
              {Object.entries(formData).map(([key, value]) => {
                if (key === 'privacyAgreed') return null;
                return (
                  <div key={key} className="border-b border-background-200/40 pb-4 last:border-b-0 last:pb-0">
                    <p className="text-[13px] text-foreground-500 tracking-[0.08em] mb-1 font-label">
                      {labelMap[key] || key}
                    </p>
                    <p className="text-[16px] text-foreground-800 tracking-[0.03em] leading-relaxed whitespace-pre-wrap">
                      {formatValue(key, value)}
                    </p>
                  </div>
                );
              })}
              <div className="border-b border-background-200/40 pb-4 last:border-b-0 last:pb-0">
                <p className="text-[13px] text-foreground-500 tracking-[0.08em] mb-1 font-label">
                  プライバシーポリシー
                </p>
                <p className="text-[16px] text-foreground-800 tracking-[0.03em] leading-relaxed">
                  同意済み
                </p>
              </div>
            </div>
          </div>

          {submitError && (
            <p className="text-[15px] text-red-500 text-center mb-6 tracking-[0.03em] leading-relaxed">{submitError}</p>
          )}

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button
              onClick={() => navigate('/contact', { state: { formData } })}
              className="px-8 py-3 border border-background-300 text-foreground-600 hover:text-foreground-800 hover:border-foreground-400 text-[15px] tracking-[0.08em] transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              修正する
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="btn-glow px-10 py-3 bg-accent-500/90 hover:bg-accent-500 text-primary-900 text-[15px] tracking-[0.1em] font-medium transition-all duration-300 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {submitting ? '送信中...' : '送信する'}
            </button>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}